<template>
    <div class="google-map-loader">
        <div
                ref="googleMap"
                class="google-map"
                :class="{
                    'google-map-marker-bubble': displayMarkerBubble,
                    'google-map-marker-dot': !displayMarkerBubble,
                }"
                :style="getClassStyle"
        />

        <div
                class="d-none"
        >
            <div
                    v-if="!isMobile"
                    ref="zoomControl"
                    class="controls zoom-control"
            >
                <b-button-group vertical>
                    <b-button
                            class="border-0"
                            type="default"
                            @click="zoomToggle(1)"
                    >
                        <i class="fas fa-plus" />
                    </b-button>
                    <hr>
                    <b-button
                            class="border-0"
                            type="default"
                            @click="zoomToggle(-1)"
                    >
                        <i class="fas fa-minus" />
                    </b-button>
                </b-button-group>
            </div>
            <div
                    ref="maptypeControl"
                    :class="{
                      'maptype-control-mobile': isMobile,
                    }"
                    class="controls maptype-control"
            >
                <b-button
                        class="border-0"
                        type="default"
                        @click="mapTypeToggle"
                >
                    <i
                            :class="[mapTypeIcon]"
                            class="fas"
                    />
                </b-button>
            </div>
        </div>
    </div>
</template>

<script>
    import './GoogleMapLoader.scss';
    import {createCustomMarker} from "../../functions/createCustomMarker";
    import { MarkerClusterer } from "@googlemaps/markerclusterer";
    import {ActiveMarkersObserver} from "../../classes/ActiveMarkersObserver";
    import isEqual from 'lodash/isEqual';
    import isEmpty from 'lodash/isEmpty';
    import {store} from "../../../../index";
    import IsMobileMixin from "@milestoneslabs/milestones-ui-kit/src/mixins/IsMobileMixin";
    import {PROPERTY_SEARCH_MAP_FIT_BOUNDS} from "../../../mixins/PropertySearchMixin";
    import {
        PROPERTY_MAP_CARD_FAVORITE_BUTTON_FAVORITE_ACTION,
        PROPERTY_LISTING_CARD_FAVORITE_BUTTON_FAVORITE_ACTION,
        FAV_CLICK_LOADING
    } from "../../../mixins/FavoriteButtonMixin";
    import ApiFilter from "@milestoneslabs/milestones-js-engine/src/Api/ApiFilter";
    import ShowNotificationMixin from '@milestoneslabs/milestones-ui-kit/src/mixins/ShowNotificationMixin';
    import PropertyInstanceAppResource
        from "@milestoneslabs/milestones-core-js/src/AppResources/PropertyInstanceAppResource";
    import PropertyInstanceAppResourceSearch from "../../../../fixtures/PropertyInstanceSearchRepository";
    import AppResourceCollection from "@milestoneslabs/milestones-js-engine/src/Data/AppResourceCollection";
    import {PROPERTY_INSTANCE} from "../../../../helpers/variables";
    import {mapGetters} from "vuex";
    import GoogleMapsLoader from "./../../../classes/GoogleMapsLoader";

    const MAP_TYPES = {
        ROADMAP: 'roadmap',
        HYBRID: 'hybrid',
    };
    export default {
        mixins: [
            IsMobileMixin,
            ShowNotificationMixin,
        ],

        props: {
            mapConfig: {
                type: Object,
                required: true,
            },
            clusterConfig: {
                type: Object,
                default: null,
            },
            markers: {
                type: Array,
                required: true,
            },
            centerOnMarker: {
                type: Boolean,
                default: false,
            },
            location: {
                type: Array,
                default: () => [],
            },
            propertyDetailsRoute: {
                type: [String, Object],
                default: 'property.details.view',
            },
            favoriteOptionEnabled: {
                type: Boolean,
                default: true,
            },
            contentHeight: {
                type: Number,
                default: 0,
            }
        },

        data() {
            return {
                google: null,
                map: null,
                displayMarkerBubble: true,
                mapType: MAP_TYPES.ROADMAP,
                lastSentCoordinates: null,
                setBounds: false,
            };
        },

        mounted() {
            this.initializeMap();
        },
        async beforeMount() {
            this.$bus.$on(PROPERTY_MAP_CARD_FAVORITE_BUTTON_FAVORITE_ACTION, this.handleChangeFavoriteEvent);
            this.$bus.$on(PROPERTY_SEARCH_MAP_FIT_BOUNDS, this.handleMapFitBounds);
        },

        beforeDestroy() {
            this.$bus.$off(PROPERTY_MAP_CARD_FAVORITE_BUTTON_FAVORITE_ACTION, this.handleChangeFavoriteEvent);
            this.$bus.$off(PROPERTY_SEARCH_MAP_FIT_BOUNDS, this.handleMapFitBounds);
        },

        computed: {
            ...mapGetters({
                coreProperty: 'getCoreProperty',
            }),
            hasMarkerClusterer() {
                return Boolean(this.clusterConfig);
            },
            mapTypeIcon() {
                return this.mapType === MAP_TYPES.ROADMAP ? 'fa-globe-americas' : 'fa-map';
            },
            getClassStyle() {
                if (this.contentHeight !== 0) {
                    return {height: `calc(100vh - ${this.contentHeight}px)`};
                }

                return {};
            }
        },

        watch: {
            markers: {
                handler (newVal, oldVal) {
                    if (this.map) {
                        this.setMapMarkers();
                        if (this.setBounds) {
                            this.setMapBounds();
                        }
                    }
                },
            }
        },

        methods: {
            async handleChangeFavoriteEvent(isFavorite, uuid = null, propertyFromMapMarker = false) {
                if (this.favoriteOptionEnabled && uuid && !!propertyFromMapMarker) {
                    this.$bus.$emit(FAV_CLICK_LOADING, true, uuid);
                    try {
                        const propertyInstance = await this.getPropertyAppData(uuid, propertyFromMapMarker);
                        if (!propertyInstance || !(propertyInstance instanceof PropertyInstanceAppResource)) {
                            throw 'No property instance found with given uuid!';
                        }
                        propertyInstance.isFavorite = "boolean" === typeof (isFavorite)
                            ? isFavorite
                            : !propertyInstance.isFavorite;
                        propertyInstance._dirty['isFavorite'] = true;

                        await propertyInstance.save();
                        this.$bus.$emit(
                            PROPERTY_LISTING_CARD_FAVORITE_BUTTON_FAVORITE_ACTION,
                            propertyInstance.isFavorite,
                            uuid
                        );
                    } catch (e) {
                        this.$bus.$emit(
                            PROPERTY_MAP_CARD_FAVORITE_BUTTON_FAVORITE_ACTION,
                            !isFavorite,
                            uuid,
                            false
                        );
                        this.showNotification({
                            message: 'Something went wrong. Please try again later',
                            type: 'error'
                        });
                        console.error(e);
                    } finally {
                        this.$bus.$emit(FAV_CLICK_LOADING, false, uuid);
                    }
                }
            },

            async getPropertyAppData(uuid, property = null) {
                if (!uuid || !this.favoriteOptionEnabled) {
                    return;
                }
                let propAppResourceSearch = new PropertyInstanceAppResourceSearch();
                propAppResourceSearch.setSearchData({
                    limit: 1,
                    filter: new ApiFilter({
                        params: {
                            propertyUuid: uuid,
                            workspaceUuid: store.getters.getWorkspaceUuid,
                        },
                    })
                }, false);
                try {
                    await propAppResourceSearch.search();
                } catch (e) {
                    this.showNotification({
                        message: 'Something went wrong. Please try again later',
                        type: 'error'
                    });
                    console.error(e);
                }

                const propertyFound = propAppResourceSearch.appResourceCollection.getFirst() || false;
                if (propertyFound) {
                    return propertyFound;
                }
                if (!!property && !!property._uuid) {
                    return await this.createPropertyInstance(property);
                }
            },

            getPropertyName(propertyAppResource) {
                if (
                    propertyAppResource.addresses &&
                    propertyAppResource.addresses instanceof AppResourceCollection &&
                    propertyAppResource.addresses.toArray().length
                ) {
                    return propertyAppResource.addresses.getFirst().fullAddress;
                } else {
                    return PROPERTY_INSTANCE.DEFAULT_NAME;
                }
            },

            async createPropertyInstance(property) {
                if (!(property || {})._uuid) return;
                const propertyInstanceAppResource = new PropertyInstanceAppResource({
                    attributes: {
                        name: this.getPropertyName(property),
                        ownerUserUuid: store.getters.authUser.uuid,
                        organizationUuid: store.getters.getOrganizationUuid,
                        workspaceUuid: store.getters.getWorkspaceUuid,
                        propertyUuid: property._uuid,
                    }
                });
                try {
                    await propertyInstanceAppResource.save();
                } catch (e) {
                    this.showNotification({
                        message: 'Something went wrong. Please try again later',
                        type: 'error'
                    });
                    console.error(e);
                }
                return propertyInstanceAppResource;
            },

            handleMapFitBounds() {
                this.setBounds = true;
            },

            getLocationCenter() {
                let googleMapsLoader = GoogleMapsLoader.instance();
                const LatLng = googleMapsLoader.google.maps.LatLng;
                const LatLngBounds = googleMapsLoader.google.maps.LatLngBounds;

                const sw = new LatLng({
                    lat: this.location[1],
                    lng: this.location[0],
                });
                let ne = new LatLng({
                    lat: this.location[3],
                    lng: this.location[2],
                });
                let bounds = new LatLngBounds(sw, ne);

                return bounds.getCenter();
            },

            setMapBounds() {
                if (!this.markers.length) {
                    return;
                }

                let googleMapsLoader = GoogleMapsLoader.instance();
                let markerBounds = new googleMapsLoader.google.maps.LatLngBounds();

                for (let marker of this.markers) {
                    markerBounds.extend(marker.latLng);
                }
                this.map.fitBounds(markerBounds);
            },

            async initializeMap() {
                let googleMapsLoader = GoogleMapsLoader.instance();
                googleMapsLoader.load();

                await googleMapsLoader.importLibrary('maps');

                let location;
                if (this.centerOnMarker) {
                    location = this.markers[0].latLng;
                } else if (this.location.length) {
                    location = await this.getLocationCenter();
                }

                this.map = new googleMapsLoader.google.maps.Map(this.$refs.googleMap,{
                    ...this.mapConfig,
                    center: location
                });

                this.map.addListener('zoom_changed', this.setMarkerBubble);
                this.map.addListener('idle', this.idleMapListener);

                if (!this.hasMarkerClusterer) {
                    this.setActiveMarkersObserver();
                }
                this.setMapMarkers();
                this.initMapControls();
            },


            setMarkerBubble() {
                let zoom = this.map.getZoom();
                this.displayMarkerBubble = zoom > 16;
            },

            idleMapListener() {
                if (this.setBounds) {
                    this.setBounds = false;
                } else {
                    let coordinates = this.map.getBounds() ? [
                        this.map.getBounds().getSouthWest().lng(),
                        this.map.getBounds().getSouthWest().lat(),
                        this.map.getBounds().getNorthEast().lng(),
                        this.map.getBounds().getNorthEast().lat(),
                    ] : [];
                    if (!isEqual(coordinates, this.lastSentCoordinates)) {
                        this.lastSentCoordinates = coordinates;
                        this.$emit('idle', this.lastSentCoordinates);
                    }
                }
            },

            zoomToggle(value) {
                this.map.setZoom(this.map.getZoom() + value);
            },

            mapTypeToggle() {
                if (this.mapType === MAP_TYPES.ROADMAP) {
                    this.mapType = MAP_TYPES.HYBRID;
                } else {
                    this.mapType = MAP_TYPES.ROADMAP;
                }

                this.map.setMapTypeId(this.mapType);
            },

            initMapControls() {
                this.map.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(
                    this.$refs.maptypeControl);
                this.map.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(
                    this.$refs.zoomControl);
            },


            setMapMarkers() {
                let customMarkers = [];
                let googleMapsLoader = GoogleMapsLoader.instance();

                for (let marker of this.markers) {
                    let favEnabled = this.favoriteOptionEnabled && !marker.attributes.isHOCoreProperty;
                    const propertyDetailsRoute = marker.attributes.isHOCoreProperty ?
                        'dashboard' : this.propertyDetailsRoute;

                    customMarkers.push(createCustomMarker(
                        googleMapsLoader.google,
                        this.map,
                        marker,
                        false,
                        this.$router,
                        this.$bus,
                        favEnabled,
                        propertyDetailsRoute,
                        !marker.attributes.isHOCoreProperty
                    ));
                }

                this.hasMarkerClusterer ?
                    this.setMarkerClusterer(customMarkers) :
                    this.notifyActiveMarkersObserver(customMarkers);
            },
            /**
             * @param customMarkers {Array.<CustomOverlayView>}
             */
            setMarkerClusterer(customMarkers) {
                if (this.map.activeMarkerCluster) {
                    this.map.activeMarkerCluster.clearMarkers();
                }
                this.map.activeMarkerCluster = new MarkerClusterer({
                    map: this.map,
                    markers: customMarkers,
                    renderer: this.clusterConfig.renderer,
                });
            },

            setActiveMarkersObserver() {
                this.map.activeMarkersObserver = new ActiveMarkersObserver();
            },
            /**
             * @param customMarkers {Array.<CustomOverlayView>}
             */
            notifyActiveMarkersObserver(customMarkers) {
                this.map.activeMarkersObserver.updateMarkerList(customMarkers);
            }
        }
    };
</script>
